import { ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Navigate, useLocation } from "react-router-dom";

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="font-display text-2xl animate-pulse">⚽ Lade …</div>
      </div>
    );
  }
  // Preserve the original search params (e.g. `?code=ABC123` from a
  // WhatsApp invite link) when bouncing to /login. The login page reads
  // `?code=` and stashes it in sessionStorage; without this, the param
  // would be dropped and the user would have to manually enter the code
  // after signing in.
  if (!user) return <Navigate to={`/login${loc.search}`} state={{ from: loc }} replace />;
  return <>{children}</>;
}

export function RequireProfile({ children }: { children: ReactNode }) {
  const { user, profile, loading } = useAuth();
  const loc = useLocation();
  if (loading) return null;
  if (!user) return <Navigate to={`/login${loc.search}`} replace />;
  // Gate on the two essentials only:
  //   - hasPassword: needed so magic-link signups can come back later
  //   - onboardingCompleted: flips true when the wizard finishes OR the user
  //     hits "Überspringen". Bonus answers are no longer required for access —
  //     they're editable later via Profil → Bonus-Fragen.
  if (!profile || !profile.hasPassword || !profile.onboardingCompleted) {
    // Same reasoning — keep `?code=` through the onboarding bounce so
    // a not-yet-onboarded user invited via WhatsApp ends up auto-joining
    // the group once they finish the wizard.
    return <Navigate to={`/onboarding${loc.search}`} replace />;
  }
  return <>{children}</>;
}
